/**
 * docsContent — loading the AstroTour[AR] reference.
 *
 * Content is plain markdown served from `public/docs/ar/<lang>/`, fetched at runtime and
 * rendered with `react-markdown` — the same mechanism the Legal pages use for
 * `privacy-policy.md`. A manifest (`index.json`) drives the sidebar, so adding a page is a
 * markdown file plus a manifest line, never a code change.
 *
 * `<lang>` exists so a translation can be dropped in later without touching the app: any
 * language not in {@link DOCS_LANGS} falls back to English.
 */
import axios from 'axios';

export interface DocsPage {
  /** URL segment: `/ar-reference/<slug>`. */
  slug: string;
  title: string;
  /** Path of the markdown file, relative to `public/docs/ar/<lang>/`. */
  file: string;
  /** One-line summary, shown on the index page. */
  summary?: string;
}

export interface DocsSection {
  title: string;
  /** Optional blurb shown under the section heading on the index page. */
  summary?: string;
  pages: DocsPage[];
}

export interface DocsManifest {
  title: string;
  sections: DocsSection[];
}

/** Languages with a translated `public/docs/ar/<lang>/` directory. */
export const DOCS_LANGS = ['en'];

export const DOCS_ROOT = '/docs/ar';

/** `en-US` → `en`; anything without translated docs → `en`. */
export function resolveDocsLang(language?: string): string {
  const base = (language || 'en').toLowerCase().split('-')[0];
  return DOCS_LANGS.includes(base) ? base : 'en';
}

export function docsFileUrl(lang: string, file: string): string {
  return `${DOCS_ROOT}/${lang}/${file}`;
}

export async function fetchDocsManifest(lang: string): Promise<DocsManifest> {
  const { data } = await axios.get(docsFileUrl(lang, 'index.json'));
  return data as DocsManifest;
}

export async function fetchDocsPage(
  lang: string,
  file: string
): Promise<string> {
  const { data } = await axios.get(docsFileUrl(lang, file));
  return typeof data === 'string' ? data : String(data);
}

/** Every page across every section, in sidebar order — used for prev/next and lookup. */
export function flattenDocsPages(manifest?: DocsManifest | null): DocsPage[] {
  if (!manifest?.sections) return [];
  return manifest.sections.reduce<DocsPage[]>(
    (all, section) => all.concat(section.pages || []),
    []
  );
}

export function findDocsPage(
  manifest: DocsManifest | null | undefined,
  slug?: string
): DocsPage | undefined {
  const pages = flattenDocsPages(manifest);
  if (!slug) return pages[0];
  return pages.find((page) => page.slug === slug);
}

/** The pages before and after `slug`, for the footer links. */
export function docsNeighbors(
  manifest: DocsManifest | null | undefined,
  slug?: string
): { prev?: DocsPage; next?: DocsPage } {
  const pages = flattenDocsPages(manifest);
  const index = slug ? pages.findIndex((page) => page.slug === slug) : 0;
  if (index < 0) return {};
  return { prev: pages[index - 1], next: pages[index + 1] };
}
